import { useState, useEffect, useCallback, useMemo } from 'react';

interface UseDateRangeSelectionProps {
  initialStartDate: string;
  initialEndDate: string;
  blockedDates: { start: string; end: string }[];
  isOpen: boolean;
}

export function useDateRangeSelection({
  initialStartDate,
  initialEndDate,
  blockedDates,
  isOpen,
}: UseDateRangeSelectionProps) {
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [selectingEnd, setSelectingEnd] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStartDate(initialStartDate);
      setEndDate(initialEndDate);
      setSelectingEnd(!!initialStartDate && !initialEndDate);
    }
  }, [isOpen, initialStartDate, initialEndDate]);

  // Build blocked dates set for fast lookup
  const blockedDatesSet = useMemo(() => {
    const set = new Set<string>();
    blockedDates.forEach(range => {
      const current = new Date(range.start + 'T12:00:00');
      const end = new Date(range.end + 'T12:00:00');
      while (current <= end) {
        const y = current.getFullYear();
        const m = String(current.getMonth() + 1).padStart(2, '0');
        const d = String(current.getDate()).padStart(2, '0');
        set.add(`${y}-${m}-${d}`);
        current.setDate(current.getDate() + 1);
      }
    });
    return set;
  }, [blockedDates]);

  // Same-day cutoff: oggi non prenotabile dopo le 17:00 (orario chiusura).
  // Calcolato fresco ad ogni render — modal mount = freshness.
  const todayCutoffStr = useMemo(() => {
    const now = new Date();
    if (now.getHours() < 17) return null;
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [isOpen]);

  const isDateBlocked = useCallback(
    (dateStr: string) => blockedDatesSet.has(dateStr) || dateStr === todayCutoffStr,
    [blockedDatesSet, todayCutoffStr]
  );

  // Calculate days
  const days = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const s = new Date(startDate + 'T12:00:00');
    const e = new Date(endDate + 'T12:00:00');
    return Math.round((e.getTime() - s.getTime()) / 86400000) + 1;
  }, [startDate, endDate]);

  const handleDayClick = useCallback((dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const clicked = new Date(dateStr + 'T12:00:00');

    // Can't select past dates
    if (clicked < today) return;

    // Can't select blocked dates
    if (isDateBlocked(dateStr)) return;

    if (!startDate || (startDate && endDate)) {
      // Starting new selection
      setStartDate(dateStr);
      setEndDate('');
      setSelectingEnd(true);
    } else if (selectingEnd) {
      // Completing selection
      if (dateStr < startDate) {
        // Clicked before start — swap
        setEndDate(startDate);
        setStartDate(dateStr);
      } else {
        // Check if any blocked date is between start and clicked
        let hasBlocked = false;
        const check = new Date(startDate + 'T12:00:00');
        const end = new Date(dateStr + 'T12:00:00');
        while (check <= end) {
          const y = check.getFullYear();
          const m = String(check.getMonth() + 1).padStart(2, '0');
          const d = String(check.getDate()).padStart(2, '0');
          if (isDateBlocked(`${y}-${m}-${d}`)) {
            hasBlocked = true;
            break;
          }
          check.setDate(check.getDate() + 1);
        }

        if (hasBlocked) {
          // Start new selection from clicked date
          setStartDate(dateStr);
          setEndDate('');
        } else {
          setEndDate(dateStr);
        }
      }
      setSelectingEnd(false);
    }
  }, [startDate, endDate, selectingEnd, isDateBlocked]);

  const handleClear = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setSelectingEnd(false);
  }, []);

  // Imposta un range a codice (scorciatoie durata: 7gg / 1 mese).
  const setRange = useCallback((s: string, e: string) => {
    setStartDate(s);
    setEndDate(e && e !== s ? e : '');
    setSelectingEnd(false);
  }, []);

  return {
    startDate,
    endDate,
    selectingEnd,
    days,
    handleDayClick,
    handleClear,
    isDateBlocked,
    setRange,
  };
}
